import {useState} from "react";
import {Alert, Box, Chip, CircularProgress, Typography} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PermissionsPreview from "../PermissionsPreview";
import UserSearchPicker from "../UserSearchPicker";

// Step 3 — Groups & Permissions. Multi-select of the groups available for the chosen (User Type,
// Business Unit); the default group is auto-selected on entry (by the shell). At least one group must
// remain selected. The read-only permission preview updates live as groups are toggled.
const Step3Groups = (ctx) => {
    const {availableGroupObjs, selectedGroupObjs, draft, setField, categories, groupsLoading, defaultGroupId, businessUnitCode, copyGroupsFromUser, copySummary} = ctx;
    const [minMsg, setMinMsg] = useState(false);

    const selectedIds = draft.groupIds ?? [];
    const isSelected = (id) => selectedIds.includes(id);

    const toggle = (id) => {
        if (isSelected(id)) {
            if (selectedIds.length <= 1) {
                setMinMsg(true);
                return;
            }
            setMinMsg(false);
            setField("groupIds", selectedIds.filter((x) => x !== id));
        } else {
            setMinMsg(false);
            setField("groupIds", [...selectedIds, id]);
        }
    };

    if (groupsLoading && availableGroupObjs.length === 0) {
        return <Box sx={{display: "flex", justifyContent: "center", p: 4}}><CircularProgress size={24} /></Box>;
    }

    if (availableGroupObjs.length === 0) {
        return (
            <Typography sx={{color: "text.secondary", fontStyle: "italic"}}>
                No groups are available for the selected Business Unit and User Type.
            </Typography>
        );
    }

    return (
        <Box sx={{maxWidth: 820, mx: "auto"}}>
            <Typography sx={{fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 1}}>
                Copy groups from another user
            </Typography>
            <UserSearchPicker businessUnitCode={businessUnitCode} onSelect={copyGroupsFromUser} />
            {copySummary && (
                <Alert severity="info" sx={{mt: 1}}>
                    Copied groups from <b>{copySummary.sourceName || "user"}</b>
                    {copySummary.groupNames.length > 0 ? ` (${copySummary.groupNames.join(", ")})` : ""}. Only groups available
                    for this User Type &amp; Business Unit are kept.
                </Alert>
            )}
            <Box sx={{borderBottom: 1, borderColor: "divider", my: 2}} />

            <Typography sx={{fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 1}}>
                Groups
            </Typography>
            <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                {availableGroupObjs.map((g) => {
                    const selected = isSelected(g.id);
                    return (
                        <Chip
                            key={g.id}
                            label={g.id === defaultGroupId ? `${g.name} (default)` : g.name}
                            onClick={() => toggle(g.id)}
                            icon={selected ? <CheckIcon /> : undefined}
                            variant={selected ? "filled" : "outlined"}
                            sx={{
                                fontWeight: selected ? 700 : 500,
                                borderColor: g.color,
                                backgroundColor: selected ? g.color : "transparent",
                                color: selected ? "white" : "text.primary",
                                "& .MuiChip-icon": {color: "white"},
                                "&:hover": {backgroundColor: selected ? g.color : "rgba(0,0,0,0.04)"},
                            }}
                        />
                    );
                })}
            </Box>
            {minMsg && (
                <Typography sx={{color: "error.main", fontSize: "0.78rem", mt: 1}}>
                    At least one group is required.
                </Typography>
            )}

            <Typography sx={{fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mt: 3, mb: 1}}>
                Permissions
            </Typography>
            <PermissionsPreview selectedGroups={selectedGroupObjs} categories={categories} />
        </Box>
    );
};

export default Step3Groups;
